import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Layers, 
  Filter, 
  ShieldAlert, 
  CheckCircle2, 
  ArrowRight, 
  Target, 
  Search, 
  Sparkles, 
  Cpu, 
  Lock,
  ChevronRight,
  Calculator,
  SlidersHorizontal,
  Flame
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const adGroups = [
  {
    id: "high-intent",
    number: "01",
    title: "High Commercial Intent",
    badge: "Bottom of Funnel",
    intent: "Ready-to-Buy / Hire",
    searcherState: "Comparing pricing, requesting proposals, ready to enter payment details.",
    exampleQueries: ["hire google ads agency", "ppc automation software pricing", "buy google ads management tool"],
    adCopyHook: "Launch in 60s · Transparent Pricing · 7-Day Free Trial",
    impact: "Captures visitors with immediate buying velocity. Lowest CPA and highest conversion rate."
  },
  {
    id: "solution",
    number: "02",
    title: "Solution & Category",
    badge: "Active Evaluation",
    intent: "Category Seekers",
    searcherState: "Actively researching platforms to solve a specific marketing bottleneck.",
    exampleQueries: ["automated google ads tool", "google ads campaign generator", "best ppc software for agencies"],
    adCopyHook: "Stop Navigating 14-Screen Google Wizards · 5 Ad Groups In 60 Seconds",
    impact: "Positions your core offer as the modern alternative to outdated manual workflows."
  },
  {
    id: "pain-point",
    number: "03",
    title: "Pain-Point & Frustration",
    badge: "Problem-Aware",
    intent: "Budget Bleeders",
    searcherState: "Experiencing burning frustration: high CPA, wasted spend, agency dissatisfaction.",
    exampleQueries: ["google ads cost too much", "reduce google ads cpa", "how to stop broad match wasted spend"],
    adCopyHook: "Cut Wasted Clicks Before They Spend · Pre-Flight CPA Math Ceilings",
    impact: "Resonates emotionally by highlighting and solving the exact reason their previous ads failed."
  },
  {
    id: "features",
    number: "04",
    title: "Feature & Capability",
    badge: "Technical Buyers",
    intent: "Specific Requirements",
    searcherState: "Looking for specific architectural capabilities, integrations, and match filters.",
    exampleQueries: ["google ads claude mcp", "negative keyword auto filter tool", "google ads api v19 campaign builder"],
    adCopyHook: "Native MCP Integration for Claude & ChatGPT · Enforce Exact CPA Ceilings",
    impact: "Appeals to developers, power marketers, and technical founders seeking granular control."
  },
  {
    id: "competitor",
    number: "05",
    title: "Competitor & Alternative",
    badge: "Switcher Intent",
    intent: "Dissatisfied Switchers",
    searcherState: "Unhappy with exorbitant agency retainers ($3k-$5k/mo) or clunky legacy software.",
    exampleQueries: ["alternative to ppc agency", "cheaper than google ads agency", "replace marketing agency with software"],
    adCopyHook: "Replace $3,000/mo Retainers · Full PPC Control From $99/mo",
    impact: "Steals market share from expensive legacy providers by presenting a 10x faster, cheaper model."
  }
];

const SystemArchitectureSection = () => {
  const [selectedGroup, setSelectedGroup] = useState(0);

  return (
    <section className="py-24 px-4 bg-slate-950 text-white relative overflow-hidden">
      {/* Background glow effects */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-blue-600/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-[400px] h-[400px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="text-center mb-16">
          <Badge className="px-3 py-1 text-sm bg-blue-500/20 text-blue-400 border-blue-500/30 mb-4">
            The System in Place
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6">
            Not a generic prompt. <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-300 to-cyan-400">
              A 5-tier programmatic campaign engine.
            </span>
          </h2>
          <p className="text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Most "AI tools" dump 200 random keywords into a single ad group that Google burns on broad match misclicks. 
            Upfixe executes an institutional-grade 5-ad-group architecture with mathematical CPA budget ceilings.
          </p>
        </div>

        {/* The 4-Stage Pre-Flight Safety Pipeline */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-20">
          <div className="p-5 rounded-xl bg-slate-900/80 border border-slate-800 relative">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-mono text-blue-400 font-semibold uppercase tracking-wider">Step 1</span>
              <Sparkles className="h-4 w-4 text-blue-400" />
            </div>
            <h4 className="font-semibold text-slate-100 mb-1">Scrape & Value Proposition</h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              Crawls your URL, extracts customer segments, UVPs, and creates a 9-section brand brief.
            </p>
          </div>

          <div className="p-5 rounded-xl bg-slate-900/80 border border-slate-800 relative">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-mono text-cyan-400 font-semibold uppercase tracking-wider">Step 2</span>
              <Search className="h-4 w-4 text-cyan-400" />
            </div>
            <h4 className="font-semibold text-slate-100 mb-1">Live Keyword Planner API</h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              Fetches 150+ real search terms, monthly volumes, and live top-of-page CPC bid data.
            </p>
          </div>

          <div className="p-5 rounded-xl bg-slate-900/80 border border-blue-500/40 relative shadow-[0_0_25px_rgba(59,130,246,0.15)]">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-mono text-emerald-400 font-semibold uppercase tracking-wider">Step 3 (The Moat)</span>
              <Calculator className="h-4 w-4 text-emerald-400" />
            </div>
            <h4 className="font-semibold text-slate-100 mb-1">Mathematical CPA Filter</h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              Instantly drops any keyword where CPC &gt; (Target CPA × Estimated Conv Rate). Zero budget suicide.
            </p>
          </div>

          <div className="p-5 rounded-xl bg-slate-900/80 border border-slate-800 relative">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-mono text-purple-400 font-semibold uppercase tracking-wider">Step 4</span>
              <Lock className="h-4 w-4 text-purple-400" />
            </div>
            <h4 className="font-semibold text-slate-100 mb-1">Negative List & Paused Deploy</h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              Synthesizes 50+ negative keywords and pushes directly to Google Ads as paused drafts.
            </p>
          </div>
        </div>

        {/* Interactive 5-Ad-Group Breakdown */}
        <div className="bg-slate-900/90 rounded-2xl border border-slate-800 p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between pb-6 border-b border-slate-800 gap-4">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-blue-400 uppercase tracking-wider mb-1">
                <Layers className="h-3.5 w-3.5" /> Programmatic Campaign Anatomy
              </div>
              <h3 className="text-2xl font-bold text-slate-100">
                The 5 Distinct Thematic Ad Groups
              </h3>
            </div>
            <p className="text-sm text-slate-400 max-w-md">
              Every campaign is isolated by search intent to preserve quality score and prevent intent bleed. Click to inspect:
            </p>
          </div>

          {/* Ad group tabs selector */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mt-6 mb-8">
            {adGroups.map((group, idx) => (
              <button
                key={group.id}
                onClick={() => setSelectedGroup(idx)}
                className={`text-left p-3 rounded-xl border transition-all text-xs flex flex-col justify-between ${
                  selectedGroup === idx
                    ? "bg-blue-600/20 border-blue-500 text-white shadow-sm"
                    : "bg-slate-950/60 border-slate-800/80 text-slate-400 hover:text-slate-200 hover:border-slate-700"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] text-blue-400 font-bold">{group.number}</span>
                  <span className={`w-1.5 h-1.5 rounded-full ${selectedGroup === idx ? "bg-blue-400" : "bg-slate-700"}`} />
                </div>
                <div className="font-medium text-slate-200 truncate">{group.title}</div>
              </button>
            ))}
          </div>

          {/* Active Ad Group Deep Dive Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedGroup}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-6 bg-slate-950/80 rounded-xl p-6 border border-slate-800"
            >
              <div className="lg:col-span-7 space-y-4">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/30">
                    {adGroups[selectedGroup].badge}
                  </Badge>
                  <span className="text-xs text-slate-500">•</span>
                  <span className="text-xs text-slate-300 font-medium">
                    Search Intent: {adGroups[selectedGroup].intent}
                  </span>
                </div>

                <h4 className="text-xl font-bold text-white">
                  {adGroups[selectedGroup].title}
                </h4>

                <p className="text-sm text-slate-400 leading-relaxed">
                  {adGroups[selectedGroup].searcherState}
                </p>

                <div className="pt-2">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    Clustered Keywords Sample
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {adGroups[selectedGroup].exampleQueries.map((query, i) => (
                      <span key={i} className="px-2.5 py-1 rounded-md bg-slate-900 border border-slate-800 text-xs font-mono text-cyan-300">
                        "{query}"
                      </span>
                    ))}
                  </div>
                </div>

                <div className="pt-2">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    RSA Ad Copy Hook
                  </div>
                  <div className="p-3 bg-blue-950/40 border border-blue-900/50 rounded-lg text-xs text-blue-200 font-medium flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-blue-400 shrink-0" />
                    "{adGroups[selectedGroup].adCopyHook}"
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5 flex flex-col justify-between p-5 rounded-xl bg-slate-900/90 border border-slate-800/80">
                <div>
                  <div className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4" /> Systemic Outcome
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed mb-4">
                    {adGroups[selectedGroup].impact}
                  </p>

                  <div className="space-y-2 pt-2 border-t border-slate-800 text-xs text-slate-400">
                    <div className="flex justify-between py-1">
                      <span>Quality Score Protection:</span>
                      <span className="text-emerald-400 font-semibold">9/10 Target</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span>Negative Exclusion Sync:</span>
                      <span className="text-blue-400 font-semibold">Cross-negated</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span>Google Ads Status:</span>
                      <span className="text-slate-300 font-semibold">Paused (0 Spend Risk)</span>
                    </div>
                  </div>
                </div>

                <Button asChild size="sm" className="mt-6 w-full bg-blue-600 hover:bg-blue-500 text-white font-medium">
                  <Link to="/how-it-works">
                    Explore Complete Methodology <ArrowRight className="ml-2 h-3.5 w-3.5" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default SystemArchitectureSection;
